import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import Spinner from '@fenderdigital/ui/Spinner';
import { actions as CollectionActions } from '@fenderdigital/business/collections';
import useFenderConnect from '@fenderdigital/custom-hooks/hooks/useFenderConnect';
import CollectionDetailHero from '../../components/LandingHeros/CollectionDetailHero';
import CollectionCourses from '../../components/CollectionCourses';
import UnSubscribedLayout from '../../components/UnSubscribedLayout';
import { RootState } from '../../redux-types/root.redux-types';
import Library from '../../lib/library';
import { getFlag } from '../../lib/feature-flags';
import { requestProgress } from '../../actions/my-progress';
import useScrollToTop from '../../hooks/useScrollToTop';
import { EventNames, trackPageEvents, getAlgoliaTracking } from '../../lib/analytics';

const { getCollectionWithID } = CollectionActions;

interface CollectionsDetailProps {
  slug: string;
}

const selector = (state: RootState) => ({
  collection: state.collections.collection,
  loadingCollection: state.collections.loadingCollection,
  coursesProgress: state.myProgress.courses,
  progressLoading: state.myProgress.inProgress,
  lessonsProgress: state.myProgress.lessons,
});

const CollectionsDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background-color: ${({ theme: { colors } }): string => colors.lightGray};
`;

const CollectionsDetail: React.FC<CollectionsDetailProps> = ({ slug }) => {
  const dispatch = useDispatch();
  const { instrument } = useParams();
  const { loggedIn, hasSubscription } = useFenderConnect();
  const {
    collection,
    loadingCollection,
    progressLoading,
    coursesProgress,
    lessonsProgress,
  } = useSelector(selector);
  const [trackingEventFired, setTrackingEventFired] = useState(false);
  useScrollToTop();

  const isPlayAuthRedirectEnabled = getFlag('isPlayAuthRedirectEnabled');
  useEffect(() => {
    dispatch(
      getCollectionWithID(slug, () =>
        Library.getCollectionBySlug(slug, instrument, loggedIn && hasSubscription),
      ),
    );
  }, [dispatch, instrument, loggedIn, slug, hasSubscription]);

  useEffect(() => {
    requestProgress(collection?.['course-ids'] ?? [], 'courses', dispatch);
  }, [collection, dispatch]);

  useEffect(() => {
    if (collection && !trackingEventFired) {
      trackPageEvents(EventNames.COLLECTION_DETAIL, getAlgoliaTracking('view', collection?.id));
      setTrackingEventFired(true);
    }
  }, [collection]);

  if (collection === null || loadingCollection || progressLoading) {
    return <Spinner fullPage />;
  }

  return (
    <>
      {isPlayAuthRedirectEnabled ? (
        <UnSubscribedLayout>
          <CollectionsDetailContainer className="collections-detail">
            <CollectionDetailHero collection={collection} loggedIn={loggedIn} />
            <CollectionCourses
              collection={collection}
              coursesProgress={coursesProgress}
              lessonsProgress={lessonsProgress}
            />
          </CollectionsDetailContainer>
        </UnSubscribedLayout>
      ) : (
        <CollectionsDetailContainer className="collections-detail">
          <CollectionDetailHero collection={collection} loggedIn={loggedIn} />
          <CollectionCourses
            collection={collection}
            coursesProgress={coursesProgress}
            lessonsProgress={lessonsProgress}
          />
        </CollectionsDetailContainer>
      )}
    </>
  );
};

export default CollectionsDetail;
